import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import moment from 'moment-timezone';
import CustomFieldDefinition, {
  buildCustomFieldValues,
  parseCFApiParamKey
} from 'volta/models/custom-field-definition';
import PlanningOrder, { POResourceName } from 'volta/models/planning-order';
import { TaskEvents } from 'volta/models/task';
import { ICollectionProperty, TCodeDescMode } from 'volta/models/types/collection-view';
import { SingleResourceDoc } from 'volta/utils/api/jsonapi-types';
import { indexByFn } from 'volta/utils/array-utils';
import { defaultErrorHandler, TError } from 'volta/utils/error-utils';
import { parseNumber } from 'volta/utils/math-utils';
import POValidations from 'volta/validations/planning-order';

import { isArray } from '@ember/array';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import { POFieldDisplayConfig } from '../utils';

import type CustomFieldDefinitionsService from 'volta/services/custom-field-definitions-service';
import type { TDisplayedPOField } from '../utils';

import type IntlService from 'ember-intl/services/intl';
import type { BufferedChangeset } from 'validated-changeset';
import type {
  IActivePlanner,
  IChangeOneStatus,
  IChangeRoadmap,
  IManyPlanningOrdersUpdatedResult,
  IUpdatePlanningOrder,
  TDateField,
  TDateFieldOptimal
} from 'volta/models/types/planning-order';
import type { IWorkshopIdentityInfo } from 'volta/models/types/workshop';
import type StoreService from 'volta/services/store';
import type AppSettings from 'volta/services/app-settings';
import type BvFlashService from 'volta/services/bv-flash';
import type BvMetricsService from 'volta/services/bv-metrics';
import type PlanningOrderApi from 'volta/services/planning-order-api';
import type SkuModalService from 'volta/services/sku-modal-service';
import type { TPOField } from 'volta/models/po-group';
import type SessionUserService from 'volta/services/session-user';
import type EventStreamService from 'volta/services/event-stream';
import type { TCustomFieldValues } from 'volta/models/types/customFieldDefinition';
import type LocalizationService from 'volta/services/localization';

const AuthorisedFieldsForDisplay: TPOField[] = [
  'sku',
  'warehouse',
  'acqCode',
  'orderNum',
  'externalOrderNum',
  'orderType',
  'supplier',
  'workshop',
  'partType',
  'deliveryWorkshop',
  'planningBuffer',
  'executionBuffer',
  'priority'
];
interface ISplit {
  reorderQty?: number;
  dateField?: Date;
  unitPrice?: number;
}

interface IArgs {
  index: number;
  planningOrder: PlanningOrder;
  ploChangeset: BufferedChangeset;
  dateField: TDateField;
  groupBy?: TPOField[];
  codeDescMode?: TCodeDescMode[];
  showSkuDesc?: boolean;
  properties?: ICollectionProperty[];
  supplierDraftSplits?: PlanningOrder[];
  selectedUser?: IActivePlanner;
  onSave?: (po: PlanningOrder) => void;
  onRoadmapChanged?: (po: PlanningOrder) => void;
  onSelect?: (selected: boolean, event: PointerEvent) => void;
  onDelete?: (po: PlanningOrder) => void;
  onLaunch?: (po: PlanningOrder) => void;
  onCommit?: (po: PlanningOrder) => void;
  onSent?: (po: PlanningOrder) => void;
  onSplit?: (po: PlanningOrder) => void;
  onFinish?: (po: PlanningOrder) => void;
  onDraft?: (po: PlanningOrder) => void;
  onRefresh?: (ids: string[]) => void;
}

export default class PlanningOrderItem extends Component<IArgs> {
  // Services
  // ~~~~~

  @service store!: StoreService;
  @service bvFlash!: BvFlashService;
  @service intl!: IntlService;
  @service skuModalService!: SkuModalService;
  @service appSettings!: AppSettings;
  @service bvMetrics!: BvMetricsService;
  @service planningOrderApi!: PlanningOrderApi;
  @service sessionUser!: SessionUserService;
  @service customFieldDefinitionsService!: CustomFieldDefinitionsService;
  @service eventStream!: EventStreamService;
  @service localization!: LocalizationService;

  // Properties
  // ~~~~~

  @tracked showDeletionConfirmModal = false;
  @tracked showFinishConfirmModal = false;
  @tracked split: ISplit = {
    reorderQty: undefined,
    dateField: undefined,
    unitPrice: this.args.planningOrder.unitPrice
  };
  @tracked isSaving: boolean = false;
  @tracked ploChangeset!: BufferedChangeset;

  resourceName = POResourceName;

  // Computed Properties
  // ~~~~~

  get isSup() {
    return this.sessionUser.user.isSupplier;
  }

  get groupBy() {
    return this.args.groupBy ?? [];
  }

  get showPriority() {
    return !!this.args.properties?.find((p) => p.property === 'priority');
  }

  @cached
  get skuCustomFields() {
    return this.buildCFValues(
      this.customFieldDefinitionsService.skusDefinitions,
      this.args.planningOrder.skuInfo.customFields
    );
  }

  @cached
  get customFields() {
    return this.buildCFValues(
      this.customFieldDefinitionsService.poDefinitions,
      this.args.planningOrder.customFields
    );
  }

  get hasSkuProperty() {
    return this.args.properties?.some((p) => p.property === 'sku');
  }

  /**
   * Returns the list of fields to display in the PO item.
   */
  @cached
  get displayedFields(): TDisplayedPOField[] {
    const { properties, planningOrder } = this.args;
    const { skuCustomFields, customFields, isSup } = this;

    return (
      properties?.reduce((acc: TDisplayedPOField[], prop) => {
        if (
          !this.groupBy.includes(prop.property as TPOField) &&
          (AuthorisedFieldsForDisplay.includes(prop.property as TPOField) || prop.isCustomField)
        ) {
          const { label, poValue, poDesc } = POFieldDisplayConfig[prop.property as TPOField] ?? {};
          let propKey = prop.property;

          if (prop.isCustomField) {
            propKey = parseCFApiParamKey(propKey);
          }

          const propIsSkuCF = prop.property.startsWith('sku.customFields');

          // Hide sku custom fields for supplier users
          if (propIsSkuCF && isSup) {
            return acc;
          }

          const cfValues = propIsSkuCF ? skuCustomFields : customFields;

          const value = prop.isCustomField
            ? cfValues[propKey]
            : poValue?.(planningOrder, this.args.codeDescMode, isSup);

          const desc = poDesc?.(planningOrder, this.args.codeDescMode, isSup);

          acc.push({
            key: prop.property,
            value,
            desc,
            isCustomField: prop.isCustomField,
            label: prop.isCustomField
              ? this.localization.tOrElse(propKey, prop.title)
              : label?.(value as string),
            color: prop.color
          } as TDisplayedPOField);
        }
        return acc;
      }, []) || []
    );
  }

  get displayedFieldsIndex() {
    return indexByFn(this.displayedFields, (f) => f.key);
  }

  get supplierDraftSplits() {
    const {
      planningOrder: { id },
      supplierDraftSplits = []
    } = this.args;
    return supplierDraftSplits.filter((p) => p.parentPoId === id);
  }

  get planningIsCalculating() {
    const { skuId } = this.args.planningOrder ?? {};
    return skuId && this.planningOrderApi.calculatingSkus.includes(skuId);
  }

  get isLoading() {
    const { planningOrder } = this.args;
    return (
      this.isSaving ||
      this.planningIsCalculating ||
      planningOrder?.isLoading ||
      planningOrder?.isReloading
    );
  }

  get splittingActions() {
    return [
      { id: 'split-line', label: 'split', action: this.handleSplit },
      { id: 'launch-one', label: 'launch', action: this.launch },
      { id: 'draft-one', label: 'poWorkbench.createDraft', action: this.draft },
      { id: 'commit-one', label: 'commit', action: this.commit },
      { id: 'send-one', label: 'send', action: this.sent },
      { id: 'finish-one', label: 'finish', action: this.finish }
    ];
  }

  get warehouseId() {
    return this.args.planningOrder.skuInfo.warehouse.warehouseId;
  }

  @cached
  get warehouseSettings() {
    return this.appSettings.byWarehouseOrDefault(this.warehouseId);
  }

  get disableEditAfterLaunched() {
    return (
      !this.isSup &&
      this.warehouseSettings?.settings.poWorkbench.disableEditAfterLaunched &&
      !this.args.planningOrder.isDraft
    );
  }

  get disableSupplierUpdate() {
    return (
      this.warehouseSettings?.settings.poWorkbench.disableSupplierUpdate &&
      !this.args.planningOrder.isDraft
    );
  }

  get disableWorkshopUpdate() {
    return (
      this.warehouseSettings?.settings.poWorkbench.disableWorkshopUpdate &&
      !this.args.planningOrder.isDraft
    );
  }

  get shouldEnableEdits() {
    const { status } = this.args.planningOrder ?? {};
    const isFirm = status === 'PROJECTED_FIRM';
    return (
      status &&
      !isFirm &&
      (['PROJECTED', 'NEW', 'DRAFT'].includes(status) || !this.disableEditAfterLaunched)
    );
  }

  get canBeSelected() {
    return this.args.planningOrder && !['PROJECTED_FIRM'].includes(this.args.planningOrder.status);
  }

  get csReorderQty() {
    return this.ploChangeset.get('proposedValues') && this.isSup
      ? this.ploChangeset.get('proposedValues.reorderQty')
      : this.ploChangeset.get('reorderQty');
  }

  get csUnitPrice() {
    return this.ploChangeset.get('proposedValues') && this.isSup
      ? this.ploChangeset.get('proposedValues.unitPrice')
      : this.ploChangeset.get('unitPrice');
  }

  get reorderQty() {
    const { reorderQty, proposedValues } = this.args.planningOrder;
    return proposedValues && this.isSup ? proposedValues.reorderQty : reorderQty;
  }

  get canDraft() {
    return (
      this.args.planningOrder &&
      ['PROJECTED', 'NEW'].includes(this.args.planningOrder.status) &&
      this.args.planningOrder.reorderQty > 0
    );
  }

  get canLaunch() {
    return (
      this.args.planningOrder &&
      ['PROJECTED', 'NEW', 'DRAFT'].includes(this.args.planningOrder.status) &&
      this.args.planningOrder.reorderQty > 0
    );
  }

  get canCommit() {
    return (
      this.args.planningOrder &&
      ['LAUNCHED'].includes(this.args.planningOrder.status) &&
      this.reorderQty > 0
    );
  }

  get canSent() {
    return (
      this.args.planningOrder &&
      ['COMMITTED'].includes(this.args.planningOrder.status) &&
      this.reorderQty > 0
    );
  }

  get canFinish() {
    return (
      this.args.planningOrder &&
      ['LAUNCHED', 'COMMITTED', 'SENT'].includes(this.args.planningOrder.status) &&
      this.args.planningOrder.reorderQty > 0
    );
  }

  get canDelete() {
    return (
      this.args.planningOrder &&
      !['NEW', 'PROJECTED', 'PROJECTED_FIRM'].includes(this.args.planningOrder.status) &&
      this.shouldEnableEdits
    );
  }

  get canSplit() {
    return this.shouldEnableEdits && this.args.planningOrder.atLeastDraft;
  }

  get canEditDate() {
    return this.shouldEnableEdits;
  }

  /**
   * Returns true if the PO can be moved to another workshop, supplier, etc.
   */
  get canMove() {
    const { planningOrder } = this.args;
    const isDistribute = planningOrder?.isDistribute;
    const statusBeforeLaunch = ['PROJECTED', 'NEW', 'DRAFT'].includes(
      planningOrder?.status as string
    );
    return planningOrder
      ? {
          orderType: statusBeforeLaunch,
          acqCode: statusBeforeLaunch,
          supplier: !this.disableSupplierUpdate || statusBeforeLaunch,
          workshop: !this.disableWorkshopUpdate || statusBeforeLaunch,
          deliveryWorkshop: !isDistribute || statusBeforeLaunch
        }
      : undefined;
  }

  /**
   * Returns true if the PO's roadmap has changed
   */
  get roadmapChanged() {
    const props = ['supplierInfo', 'workshopInfo', 'deliveryWorkshopInfo', 'acqCode', 'orderType'];
    const changes = this.ploChangeset.changes;
    return changes.any((item: { key: string }) => {
      return props.includes(item.key);
    });
  }

  /**
   * Temporary reorder and dueDate split split
   * formatted to match the API Status change commands
   */
  get formattedSplit(): IChangeOneStatus {
    const { reorderQty, dateField, unitPrice } = this.split;
    return {
      reorderQty,
      dateField: this.args.dateField,
      date: dateField ? moment(dateField).format('YYYY-MM-DD') : null,
      unitPrice
    };
  }

  /**
   * Authorized Order types for the PO warehouse
   */
  get poTypes() {
    const config = this.appSettings.byWarehouseOrDefault(
      this.args.planningOrder?.skuInfo.warehouse.warehouseId
    );
    return (config?.settings.poWorkbench.authorizedOrderTypes ?? []) as string[];
  }

  /**
   * PO optimal due, delivery of reorder date
   */
  get poDateFieldOptimal(): Date | undefined {
    const dateKey = `${this.args.dateField}Optimal` as TDateFieldOptimal;
    return this.args.planningOrder[dateKey];
  }

  /**
   * PO due, delivery of reorder date
   */
  get poDateField() {
    return this.args.planningOrder[`${this.args.dateField}` as keyof PlanningOrder] as Date;
  }

  get schedulingAlertData() {
    const {
      alerts = {},
      stockOutDate,
      dueDate,
      dueDateOptimal,
      deliveryDateOptimal,
      deliveryDate,
      reorderQty
    } = this.args.planningOrder ?? {};
    const { scheduling } = alerts;
    const optimalFlowDate =
      this.args.dateField === 'deliveryDate' ? deliveryDateOptimal : dueDateOptimal;
    const dueOrDeliveryDate = this.args.dateField === 'deliveryDate' ? deliveryDate : dueDate;

    if (scheduling) {
      const dateIsOptimal = moment(optimalFlowDate).isSame(dueOrDeliveryDate);
      const dateIsBefore = moment(dueOrDeliveryDate).isBefore(optimalFlowDate);
      const isOptimal = scheduling.criticality === 2 && dateIsOptimal;
      const { realQtyNeeded = 0 } = scheduling.payload;
      const actualQtyNeeded = !moment(dueDate).isBefore(stockOutDate)
        ? realQtyNeeded - reorderQty
        : realQtyNeeded;
      const needsMoreQty = actualQtyNeeded > 0;
      return {
        criticality: scheduling.criticality,
        dateIsOptimal,
        primaryIcon: needsMoreQty ? 'time' : 'time-outline',
        secondaryIcon:
          scheduling.criticality === 3
            ? 'help'
            : dateIsBefore
              ? needsMoreQty
                ? 'caret-down'
                : 'caret-down-outline'
              : !dateIsOptimal
                ? needsMoreQty
                  ? 'caret-up'
                  : 'caret-up-outline'
                : 'reorder-two',
        description: isOptimal
          ? this.intl.t('poWorkbench.alerts.scheduling.optimal', {
              userName: this.args.selectedUser?.firstName ?? this.args.selectedUser?.login,
              missingQty: actualQtyNeeded ?? this.intl.formatNumber(actualQtyNeeded)
            })
          : this.intl.t(`poWorkbench.alerts.scheduling.${scheduling.criticality}.description`, {
              stockOutDate,
              realQtyNeeded: realQtyNeeded && this.intl.formatNumber(realQtyNeeded),
              optimalFlowDate,
              missingQty: actualQtyNeeded ?? this.intl.formatNumber(actualQtyNeeded)
            }),
        recommendation:
          actualQtyNeeded &&
          this.intl.t(
            `poWorkbench.alerts.scheduling.${actualQtyNeeded > 0 ? 'increase' : 'decrease'}`,
            { qty: this.intl.formatNumber(reorderQty + actualQtyNeeded) }
          ),
        recommendationAction: actualQtyNeeded
          ? async () => {
              this.handleReorderQtyChange(realQtyNeeded);
              return await this.handleUpdate();
            }
          : undefined
      };
    }
    return undefined;
  }

  // Lifecycle Hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.eventStream.addHandler(
      POResourceName.plural,
      [
        TaskEvents.TaskCreated,
        TaskEvents.TaskDeleted,
        TaskEvents.TaskReopened,
        TaskEvents.TaskResolved
      ],
      this._onTaskEvent,
      this
    );
    this.initChangeset();
    this.resetSplit();
  }

  willDestroy(): void {
    this.eventStream.removeHandlers(this);
  }

  // Actions
  // ~~~~~

  @action
  handlePoUpdate() {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    this.initChangeset();
    this.resetSplit();
  }

  @action
  handleCustomFieldsChange(cfValues: TCustomFieldValues, isInvalid: boolean) {
    this.ploChangeset.set('customFields', { ...cfValues });
    this.ploChangeset.set('isCustomFieldsInvalid', isInvalid);
    this.handleUpdate();
  }

  @action
  select(selected: boolean, event: PointerEvent) {
    this.trackAction('select');
    this.args.onSelect?.(selected, event);
  }

  @action
  delete() {
    this.trackAction('delete');
    this.showDeletionConfirm();
  }

  @action
  async handleUpdate() {
    this.trackAction('handleUpdate');
    const cs = this.ploChangeset;
    const { dateField } = this.args;
    const { isSup } = this;

    const getValue = (field: string) =>
      isSup ? cs.get(`proposedValues.${field}`) ?? cs.get(field) : cs.get(field);

    await cs.validate();

    if (cs.isValid) {
      this.isSaving = true;
      const changedFields = cs.changes?.map((c: any) => c.key);
      const data = {
        reorderQty: getValue('reorderQty'),
        date: moment(getValue(dateField)).format('YYYY-MM-DD'),
        dateField: dateField,
        unitPrice: getValue('unitPrice'),
        orderType: cs.orderType,
        priority: cs.priority === 'none' ? null : cs.priority,
        forceValidation: cs.forceValidation === 'manual' || Boolean(cs.forceValidation),
        groupBy: this.groupBy
      } as IUpdatePlanningOrder;

      if (changedFields.includes('customFields')) {
        const {
          customFieldDefinitionsService: { poDefinitions }
        } = this;
        data.customFields = (cs.customFields as TCustomFieldValues)
          ? buildCustomFieldValues(poDefinitions, cs.customFields as TCustomFieldValues)
          : undefined;
      }
      try {
        const po: PlanningOrder = await this.args.planningOrder.updatePlanningOrder(data);
        return this._onUpdatePOSuccess(po, false);
      } catch (error) {
        return this._onUpdatePOFailure(error);
      }
    }
  }

  @action
  async handleChangeRoadmap() {
    this.trackAction('handleChangeRoadmap');
    const roadmapChanged = this.roadmapChanged;
    const cs = this.ploChangeset;

    await cs.validate();

    if (cs.isValid && roadmapChanged) {
      this.isSaving = true;
      const data = {
        acqCode: cs.acqCode,
        deliveryWorkshopId: cs.get('deliveryWorkshopInfo.workshopId'),
        workshopId: cs.get('workshopInfo.workshopId'),
        supplierId: cs.get('supplierInfo.supplierId'),
        setAsDefault: false,
        groupBy: this.groupBy
      } as IChangeRoadmap;

      try {
        const updatedPO: PlanningOrder = await this.args.planningOrder.changeRoadmap(data);
        return this._onUpdatePOSuccess(updatedPO, roadmapChanged);
      } catch (error) {
        return this._onUpdatePOFailure(error);
      }
    }
  }

  @action
  handleReorderQtyChange(value: number) {
    this.trackAction('handleReorderQtyChange', value + '');
    this.ploChangeset.set('reorderQty', value);

    if (this.isSup) {
      this.ploChangeset.set('proposedValues.reorderQty', value);
    }
  }

  @action
  handleUnitPriceChange(value: number) {
    this.trackAction('handleUnitPriceChange', value + '');
    this.ploChangeset.set('unitPrice', value);

    if (this.isSup) {
      this.ploChangeset.set('proposedValues.unitPrice', value);
    }
  }

  @action
  handleForceValidationChange([value]: ['manual' | 'auto']) {
    this.trackAction('handleForceValidationChange', value);
    if (!this.isSup) {
      this.ploChangeset.set('forceValidation', value === 'manual');
    }
  }

  @action
  async handleReorderQtyCommit(value: number) {
    this.trackAction('hendleReorderQtyCommit', value + '');
    this.ploChangeset.set('reorderQty', value);

    if (this.isSup) {
      this.ploChangeset.set('proposedValues.reorderQty', value);
    }
    return await this.handleUpdate();
  }

  @action
  async handleAcceptProposal() {
    this.trackAction('handleAcceptProposal');

    const cs = this.ploChangeset;

    await cs.validate();

    if (cs.isValid) {
      this.isSaving = true;

      try {
        const po: PlanningOrder = await this.args.planningOrder.acceptOneSupplierProposal({
          groupBy: this.groupBy
        });
        return this._onUpdatePOSuccess(po, false);
      } catch (error) {
        return this._onUpdatePOFailure(error);
      }
    }
  }

  @action
  async handleRefuseProposal() {
    this.trackAction('handleRefuseProposal');

    const cs = this.ploChangeset;

    await cs.validate();

    if (cs.isValid) {
      this.isSaving = true;

      try {
        const po: PlanningOrder = await this.args.planningOrder.refuseOneSupplierProposal({
          groupBy: this.groupBy
        });
        return this._onUpdatePOSuccess(po, false);
      } catch (error) {
        return this._onUpdatePOFailure(error);
      }
    }
  }

  @action
  async handleSendProposal() {
    this.trackAction('handleSendProposal');

    const cs = this.ploChangeset;

    await cs.validate();

    if (cs.isValid) {
      this.isSaving = true;

      try {
        const po: PlanningOrder = await this.args.planningOrder.sendOneSupplierProposal({
          groupBy: this.groupBy
        });
        return this._onUpdatePOSuccess(po, false);
      } catch (error) {
        return this._onUpdatePOFailure(error);
      }
    }
  }

  @action
  async handleRevertToInitial() {
    this.trackAction('handleRevertToInitial');
    const { initialValues } = this.args.planningOrder;
    if (initialValues && !this.isSup) {
      this.ploChangeset.set('reorderQty', initialValues.reorderQty);
      this.ploChangeset.set('dueDate', initialValues.dueDate);
      this.ploChangeset.set('deliveryDate', initialValues.deliveryDate);
      this.ploChangeset.set('reorderDate', initialValues.reorderDate);

      if (initialValues.unitPrice) {
        this.ploChangeset.set('unitPrice', initialValues.unitPrice);
      }
    }
    return await this.handleUpdate();
  }

  @action
  handleOrderNumUpdate(result: IManyPlanningOrdersUpdatedResult) {
    this.args.onRefresh?.(result.ok);
  }

  @action
  async launch() {
    this.trackAction('launch');
    try {
      const po: PlanningOrder = await this.args.planningOrder.launchOne({
        ...this.formattedSplit,
        groupBy: this.groupBy
      });
      return this._onLaunchSuccess(po);
    } catch (error) {
      return this._onLaunchFailure(error);
    }
  }

  @action
  async draft() {
    this.trackAction('draft');
    try {
      const po: PlanningOrder = await this.args.planningOrder.draftOne({
        ...this.formattedSplit,
        groupBy: this.groupBy
      });
      return this._onDraftSuccess(po);
    } catch (error) {
      return this._onDraftFailure(error);
    }
  }

  @action
  async commit() {
    this.trackAction('commit');
    try {
      const po: PlanningOrder = await this.args.planningOrder.commitOne({
        ...this.formattedSplit,
        groupBy: this.groupBy
      });
      return this._onCommitSuccess(po);
    } catch (error) {
      return this._onCommitFailure(error);
    }
  }

  @action
  async sent() {
    this.trackAction('sent');
    try {
      const po: PlanningOrder = await this.args.planningOrder.sendOne({
        ...this.formattedSplit,
        groupBy: this.groupBy
      });
      return this._onSendSuccess(po);
    } catch (error) {
      return this._onSendFailure(error);
    }
  }

  @action
  finish() {
    this.trackAction('finish');
    this.showFinishConfirmModal = true;
  }

  @action
  async handleSplit() {
    this.trackAction('split');
    try {
      const po: PlanningOrder = await this.args.planningOrder.split({
        ...this.formattedSplit,
        groupBy: this.groupBy
      });
      return this._onSplitSuccess(po);
    } catch (error) {
      return this._onSplitFailure(error);
    }
  }

  @action
  cancelFinish() {
    this.trackAction('cancelFinish');
    this.showFinishConfirmModal = false;
  }

  @action
  async confirmFinish() {
    this.trackAction('confirmFinish');
    try {
      const po: PlanningOrder = await this.args.planningOrder.finishOne({
        ...this.formattedSplit,
        groupBy: this.groupBy
      });
      return this._onFinishSuccess(po);
    } catch (error) {
      return this._onFinishFailure(error);
    }
  }

  @action
  rollback() {
    this.trackAction('rollback');
    this.ploChangeset?.rollback();
    this.resetSplit();
  }

  @action
  async confirmDeletion() {
    this.trackAction('confirmDeletion');
    try {
      const po: PlanningOrder = await this.args.planningOrder.deleteOne({
        groupBy: this.groupBy
      });
      return this._onDeleteSuccess(po);
    } catch (error) {
      return this._onDeleteFailure(error);
    }
  }

  @action
  async handleDateFieldChange(date: Date | string) {
    this.trackAction('handleDateFieldChange');
    const jsDate = moment(date).toDate();
    this.ploChangeset.set(this.args.dateField, jsDate);

    if (this.isSup) {
      this.ploChangeset.set(`proposedValues.${this.args.dateField}`, jsDate);
    }

    return await this.handleUpdate();
  }

  @action
  async handlePriorityChange(priority: number | null) {
    this.trackAction('handlePriorityChange');
    this.ploChangeset.set('priority', priority);
    return await this.handleUpdate();
  }

  @action
  changeSplitDueDate(dueDate: Date | string) {
    this.trackAction('changeSplitDueDate');
    this.split = { ...this.split, dateField: moment(dueDate).toDate() };
  }

  @action
  changeSplitUnitPrice(unitPrice: number) {
    this.trackAction('changeSplitUnitPrice');
    this.split = { ...this.split, unitPrice };
  }

  @action
  cancelDeletion() {
    this.trackAction('cancelDeletion');
    this.closeDeletionConfirm();
  }

  @action
  changeSupplier(supplierId: string) {
    this.trackAction('changeSupplier', supplierId);
    const supplierInfo = Object.assign(
      { supplierId: undefined },
      this.ploChangeset.get('supplierInfo')
    );
    supplierInfo.supplierId = supplierId;
    this.ploChangeset.set('supplierInfo', supplierInfo);
  }

  @action
  changeWorkshop(workshopId: string, workshopInfo: IWorkshopIdentityInfo) {
    this.trackAction('changeWorkshop', workshopId);
    this.ploChangeset.set('workshopInfo', { ...workshopInfo, workshopId });
  }

  @action
  changeDeliveryWorkshop(workshopId: string, workshopInfo: IWorkshopIdentityInfo) {
    this.trackAction('changeDeliveryWorkshop', workshopId);
    this.ploChangeset.set('deliveryWorkshopInfo', { ...workshopInfo, workshopId });
  }

  @action
  handleSplitReorderQtyChange(value: number) {
    this.trackAction('handleSplitReorderQtyChange', value + '');
    this.split = { ...this.split, reorderQty: parseNumber(value) };
  }

  // Helper Functions
  // ~~~~~

  initChangeset() {
    this.ploChangeset = Changeset(
      this.args.planningOrder,
      lookupValidator(POValidations),
      POValidations
    );
  }

  closeDeletionConfirm() {
    this.showDeletionConfirmModal = false;
  }

  showDeletionConfirm() {
    this.showDeletionConfirmModal = true;
  }

  resetSplit() {
    if (this.isDestroyed) {
      return;
    }
    const { isSup } = this;
    const { planningOrder, dateField } = this.args;
    const po = this.ploChangeset ?? planningOrder;

    const getValue = (path: string) =>
      isSup ? po.get(`proposedValues.${path}`) ?? po.get(path) : po.get(path);

    this.split = {
      reorderQty: getValue('reorderQty'),
      dateField: getValue(dateField),
      unitPrice: getValue('unitPrice')
    };
  }

  // Private Callbacks/Handlers
  // ~~~~~

  private _onUpdatePOSuccess(updatedPO: PlanningOrder, roadmapChanged: boolean) {
    this.bvFlash.success(this.intl.t('poWorkbench.savePloSuccess'));
    this.args.onSave?.(updatedPO);

    this.ploChangeset?.rollback();
    this.resetSplit();
    this.isSaving = false;

    if (roadmapChanged) {
      return this.args.onRoadmapChanged?.(updatedPO);
    }

    return updatedPO;
  }

  private _onUpdatePOFailure(error: TError) {
    defaultErrorHandler(error);
    this.resetSplit();
    this.isSaving = false;
    this.bvFlash.apiError(error, { fallbackMessage: 'poWorkbench.savePloError' });
  }

  private _onLaunchSuccess(launchedPO: PlanningOrder) {
    this.resetSplit();
    this.ploChangeset?.rollback();
    this.bvFlash.success(this.intl.t('poWorkbench.launchManyPloSuccess', { count: 1 }));
    this.args.onLaunch?.(launchedPO);
    return launchedPO;
  }

  private _onLaunchFailure(error: TError) {
    defaultErrorHandler(error);
    this.resetSplit();
    this.bvFlash.apiError(error, { fallbackMessage: 'poWorkbench.launchManyPloError' });
    return;
  }

  private _onDraftSuccess(po: PlanningOrder) {
    this.resetSplit();
    this.ploChangeset?.rollback();
    this.bvFlash.success(this.intl.t('poWorkbench.draftManyPloSuccess', { count: 1 }));
    this.args.onDraft?.(po);
    return po;
  }

  private _onDraftFailure(error: TError) {
    defaultErrorHandler(error);
    this.resetSplit();
    this.bvFlash.apiError(error, { fallbackMessage: 'poWorkbench.draftManyPloError' });
    return;
  }

  private _onCommitSuccess(commitPO: PlanningOrder) {
    this.resetSplit();
    this.ploChangeset?.rollback();
    this.bvFlash.success(this.intl.t('poWorkbench.commitManyPloSuccess', { count: 1 }));
    this.args.onCommit?.(commitPO);
    return commitPO;
  }

  private _onCommitFailure(error: TError) {
    defaultErrorHandler(error);
    this.resetSplit();
    this.bvFlash.apiError(error);
  }

  private _onSendSuccess(sentPO: PlanningOrder) {
    this.resetSplit();
    this.ploChangeset?.rollback();
    this.bvFlash.success(this.intl.t('poWorkbench.sentManyPloSuccess', { count: 1 }));
    this.args.onSent?.(sentPO);
    return sentPO;
  }

  private _onSendFailure(error: TError) {
    defaultErrorHandler(error);
    this.resetSplit();
    this.bvFlash.apiError(error);
  }

  private _onSplitSuccess(splitPO: PlanningOrder) {
    this.resetSplit();
    this.ploChangeset?.rollback();
    this.bvFlash.success(this.intl.t('poWorkbench.splitPloSuccess'));
    this.args.onSplit?.(splitPO);
    return splitPO;
  }

  private _onSplitFailure(error: TError) {
    defaultErrorHandler(error);
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    this.resetSplit();
    this.bvFlash.apiError(error);
  }

  private _onFinishSuccess(finishedPO: PlanningOrder) {
    this.resetSplit();
    this.bvFlash.success(this.intl.t('poWorkbench.finishManyPloSuccess', { count: 1 }));
    this.args.onFinish?.(finishedPO);
    this.showFinishConfirmModal = false;
    return finishedPO;
  }

  private _onFinishFailure(error: TError) {
    defaultErrorHandler(error);
    this.resetSplit();
    this.showFinishConfirmModal = false;
    this.bvFlash.apiError(error);
  }

  private _onDeleteSuccess(deletedPO: PlanningOrder) {
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    this.closeDeletionConfirm();
    this.bvFlash.success(this.intl.t('poWorkbench.ploDeletionSuccess', { count: 1 }));
    this.args.onDelete?.(deletedPO);
    deletedPO.unloadRecord();
    this.closeDeletionConfirm();
  }

  private _onDeleteFailure(error: TError) {
    defaultErrorHandler(error);
    this.bvFlash.apiError(error);
    this.closeDeletionConfirm();
  }

  /**
   * This event handler is called when a task is created, deleted, resolved or reopened and updates the comment count
   * @param payload Task event payload
   * @param event Task event type
   */
  private _onTaskEvent(
    payload: SingleResourceDoc,
    event: 'TaskCreated' | 'TaskDeleted' | 'TaskResolved' | 'TaskReopened'
  ) {
    const poId = payload.data.attributes?.linkedEntityId as string | undefined;
    const isComment = payload.data.attributes?.taskType === 'COMMENT';
    if (poId && this.args.planningOrder.id === poId && isComment) {
      let count = this.args.planningOrder.commentsCount ?? 0;
      switch (event) {
        case 'TaskCreated':
        case 'TaskReopened':
          count++;
          break;
        case 'TaskDeleted':
        case 'TaskResolved':
          count--;
          break;
      }
      this.args.planningOrder.tasks = {
        ...(this.args.planningOrder.tasks ?? {}),
        COMMENT: { total: count, due: 0, maxCriticality: 0 }
      };
    }
  }

  private trackAction(actionName: string, label?: string, value?: any) {
    return this.bvMetrics.trackAction(this, actionName, label, value);
  }

  /**
   * Builds the custom fields values for the PO item.
   * Filtering out the ones that are not authorized for the PO warehouse/acqCode
   * @param defs Custom field definitions
   * @param customFieldValues
   * @private
   */
  private buildCFValues(
    defs: CustomFieldDefinition[] = [],
    customFieldValues: TCustomFieldValues = {}
  ) {
    const { warehouseId, acqCode } = this.args.planningOrder;
    return defs.reduce((acc: Record<string, unknown>, def) => {
      const value = def.isAuthorized(warehouseId, acqCode)
        ? this.formatCustomField(def, customFieldValues)
        : undefined;
      if (value) {
        acc[def.slug] = value;
      }
      return acc;
    }, {});
  }

  private formatCustomField(def: CustomFieldDefinition, customFields: Record<string, unknown>) {
    let value = customFields[def.slug];
    if (typeof value === 'undefined') {
      return undefined;
    }

    if (def.fieldType === 'DATE') {
      value = moment(value as string).format('L');
    } else if (def.fieldType === 'NUMBER') {
      const nb = parseNumber(value);
      value = nb ? this.intl.formatNumber(nb) : undefined;
    } else if (def.fieldType === 'BOOLEAN') {
      value = this.intl.t(`${value}`.toLowerCase() === 'true' ? 'yes' : 'no');
    } else if (def.fieldType === 'ARRAY' && isArray(value)) {
      value = (value as string[]).join(', ');
    } else if (def.fieldType === 'TEXT') {
      value = `${value}`;
    } else {
      value = undefined;
    }

    return value;
  }
}
